import React from 'react';
import { Trash2, Wand2 } from 'lucide-react';
import {
  fmtINR, inputCls, r2, computeGstLine, guessHead, REVENUE_HEADS, UNITS,
  AutoGrowTextarea,
} from './shared';
import { PartChip } from './PartPicker';
import { HsnInput, LineTotalInput } from './LineInputs';

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    {children}
    {hint && <div className="mt-0.5 text-[10px] text-slate-400">{hint}</div>}
  </div>
);

/**
 * One line item as a stacked card — the phone/tablet view of the items table.
 *
 * The 9-column table is unusable below ~768px: Qty, Rate, Disc %, GST % and the
 * line total all sit outside the viewport behind a horizontal scroll, so a
 * quote cannot actually be priced on a phone. This renders the same fields,
 * same handlers and same math, stacked and thumb-sized instead.
 */
const ItemCard = ({
  item, index, isGstRow, showRevenueHead, showHsn, canDelete,
  onChange, onDelete, onUnlinkPart, hsnList = [],
}) => {
  const line = isGstRow ? computeGstLine(item) : null;
  const hasTarget = isGstRow
    && item.line_total_target !== '' && item.line_total_target != null
    && Number(item.line_total_target) > 0;
  const qtyN = Number(item.qty) || 0;
  const plainAmount = (Number(item.qty || 0) * Number(item.rate || 0)) || 0;
  const rateDisplay = hasTarget ? (qtyN ? r2(line.gross / qtyN) : line.gross) : item.rate;
  const amountDisplay = isGstRow ? (line.total || 0) : plainAmount;

  return (
    <div className="border-t border-slate-200 p-4 space-y-3" data-testid={`item-card-${index}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center justify-center h-6 min-w-6 px-2 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
          {index + 1}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-slate-900 tabular-nums" data-testid={`item-card-amount-${index}`}>
            ₹ {fmtINR(amountDisplay)}
          </span>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-2 rounded-lg hover:bg-red-50 text-red-600"
              aria-label={`Remove item ${index + 1}`}
              data-testid={`item-card-del-${index}`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <Field label="Item">
        <AutoGrowTextarea
          minRows={1}
          value={item.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Item name"
          className={`${inputCls} resize-none leading-snug overflow-hidden`}
          data-testid={`item-card-name-${index}`}
        />
        <PartChip item={item} index={`card-${index}`} onUnlink={onUnlinkPart} />
      </Field>

      <Field label="Description">
        <AutoGrowTextarea
          minRows={2}
          value={item.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Optional — specifications, notes"
          className={`${inputCls} resize-y leading-snug overflow-hidden`}
          data-testid={`item-card-desc-${index}`}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Qty">
          <input
            type="number" inputMode="decimal" min="0" step="0.01"
            value={item.qty}
            onChange={(e) => onChange({ qty: e.target.value })}
            className={`${inputCls} text-right`}
            data-testid={`item-card-qty-${index}`}
          />
        </Field>
        <Field label="Rate (₹)" hint={hasTarget ? 'From locked total' : undefined}>
          <input
            type="number" inputMode="decimal" min="0" step="0.01"
            value={rateDisplay}
            onChange={(e) => onChange({ rate: e.target.value })}
            disabled={hasTarget}
            className={`${inputCls} text-right ${hasTarget ? 'bg-slate-100 text-slate-500 font-mono' : ''}`}
            data-testid={`item-card-rate-${index}`}
          />
        </Field>
      </div>

      {showHsn && (
        <Field label="HSN / SAC">
          <HsnInput value={item.hsn_code} list={hsnList} onPick={onChange}
            className={`${inputCls} font-mono`} testId={`item-card-hsn-${index}`} />
        </Field>
      )}

      {isGstRow && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Disc %">
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                value={item.discount_pct ?? ''}
                onChange={(e) => onChange({ discount_pct: e.target.value })}
                className={`${inputCls} text-right`}
                placeholder="0"
                data-testid={`item-card-disc-${index}`}
              />
            </Field>
            <Field label="GST %">
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                value={item.gst_rate ?? ''}
                onChange={(e) => onChange({ gst_rate: e.target.value })}
                className={`${inputCls} text-right`}
                placeholder="18"
                data-testid={`item-card-gst-${index}`}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit">
              <select
                value={item.unit || 'NOS'}
                onChange={(e) => onChange({ unit: e.target.value })}
                className={inputCls}
                data-testid={`item-card-unit-${index}`}
              >
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Total (incl. GST)" hint="Type a round figure — the rate back-calculates">
            <div className="flex items-center gap-2">
              <LineTotalInput
                target={item.line_total_target}
                computed={line.total}
                onChange={(v) => onChange({ line_total_target: v })}
                className={`${inputCls} text-right font-mono font-bold ${hasTarget ? 'border-teal-400 bg-teal-50 text-teal-800' : 'text-teal-700'}`}
                data-testid={`item-card-total-${index}`}
              />
              <button
                type="button"
                onClick={() => line.total > 0 && onChange({ line_total_target: String(Math.round(line.total)) })}
                className="p-2.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 shrink-0"
                aria-label="Round off to nearest rupee"
                data-testid={`item-card-round-${index}`}
              >
                <Wand2 size={16} />
              </button>
            </div>
            {hasTarget && (
              <button
                type="button"
                onClick={() => onChange({ line_total_target: '' })}
                className="mt-1.5 text-[11px] text-slate-400 hover:text-red-500 underline"
                data-testid={`item-card-total-clear-${index}`}
              >
                clear — rate drives total
              </button>
            )}
          </Field>
        </>
      )}

      {showRevenueHead && (
        <Field label="Revenue head">
          <select
            value={item.revenue_head || guessHead(`${item.name || ''} ${item.description || ''}`) || ''}
            onChange={(e) => onChange({ revenue_head: e.target.value })}
            className={inputCls}
            data-testid={`item-card-head-${index}`}
          >
            <option value="">— Auto-suggested —</option>
            {REVENUE_HEADS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </Field>
      )}
    </div>
  );
};

export default ItemCard;
